const SCAPP_CONTEXT_SCHEMA_VERSION = 1;
const MAX_CONTEXT_BYTES = 10 * 1024 * 1024;
const MAX_BLOCKS = 32;
const MAX_INITIAL_BLOCK_CANDIDATES = 12;

const METRIC_DEFINITIONS = {
  delivered_food: {
    label: "Alimento entregado",
    aggregation: "sum",
    formulaConfirmed: true,
  },
  sfr_value: {
    label: "SFR",
    aggregation: "average",
    formulaConfirmed: true,
  },
  alarms_count: {
    label: "Alarmas",
    aggregation: "sum",
    formulaConfirmed: true,
  },
  perdidos_count: {
    label: "PPA",
    aggregation: "sum",
    formulaConfirmed: true,
  },
  visitas_count: {
    label: "Visitas",
    aggregation: "sum",
    formulaConfirmed: true,
  },
  alarms_by_ton: {
    label: "Alarmas por tonelada",
    aggregation: "sum_of_visible_daily_metric",
    formulaConfirmed: true,
  },
  alarms_by_visits: {
    label: "Alarmas por visita",
    aggregation: "sum_of_visible_daily_metric",
    formulaConfirmed: false,
  },
  fasts_count: {
    label: "Ayunos",
    aggregation: "sum",
    formulaConfirmed: true,
  },
};

const METRIC_KEYS = Object.keys(METRIC_DEFINITIONS);
const SUM_METRICS = new Set([
  "delivered_food",
  "alarms_count",
  "perdidos_count",
  "visitas_count",
  "alarms_by_ton",
  "alarms_by_visits",
  "fasts_count",
]);
const AVG_METRICS = new Set(["sfr_value"]);

function isPlainObject(value) {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function toNumber(value) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

function roundMetric(value) {
  if (!Number.isFinite(value)) return 0;
  return Math.round(value * 100) / 100;
}

function safeJsonByteLength(value) {
  try {
    return new TextEncoder().encode(JSON.stringify(value)).length;
  } catch {
    return 0;
  }
}

function cloneDeepSafe(value) {
  return JSON.parse(JSON.stringify(value));
}

function normalizeMetricValue(value = {}) {
  if (!isPlainObject(value)) return {};

  const next = { ...value };
  if (Object.prototype.hasOwnProperty.call(next, "visitas_cout")) {
    next.visitas_count = next.visitas_cout;
    delete next.visitas_cout;
  }
  if (Object.prototype.hasOwnProperty.call(next, "perdidos_cout")) {
    next.perdidos_count = next.perdidos_cout;
    delete next.perdidos_cout;
  }
  return next;
}

function normalizeValuesSummary(valuesSummary) {
  if (!valuesSummary) return [];

  if (Array.isArray(valuesSummary)) {
    return valuesSummary
      .filter((entry) => entry && typeof entry.day === "string")
      .map((entry) => ({
        day: entry.day,
        value: normalizeMetricValue(entry.value || {}),
      }))
      .sort((left, right) => left.day.localeCompare(right.day));
  }

  if (isPlainObject(valuesSummary)) {
    return Object.entries(valuesSummary)
      .map(([day, value]) => ({
        day,
        value: normalizeMetricValue(value || {}),
      }))
      .sort((left, right) => left.day.localeCompare(right.day));
  }

  return [];
}

function normalizeAlarmBands(input) {
  if (!input) return [];

  if (Array.isArray(input)) {
    return input
      .filter((entry) => entry && typeof entry.day === "string")
      .map((entry) => ({
        day: entry.day,
        value: normalizeAlarmBandValue(entry.value),
      }));
  }

  if (isPlainObject(input)) {
    return Object.entries(input).map(([day, value]) => ({
      day,
      value: normalizeAlarmBandValue(value),
    }));
  }

  return [];
}

function normalizeAlarmBandValue(value) {
  if (!isPlainObject(value)) return { baja: 0, media: 0, alta: 0 };

  const readBand = (key) => {
    const band = value[key];
    if (isPlainObject(band) && Number.isFinite(Number(band.value))) {
      return toNumber(band.value);
    }
    if (isPlainObject(band) && Number.isFinite(Number(band.alarm))) {
      return toNumber(band.alarm);
    }
    return toNumber(band);
  };

  return {
    baja: readBand("baja"),
    media: readBand("media"),
    alta: readBand("alta"),
  };
}

function normalizeTimeSeries(input, stripSnapshots = false) {
  if (!Array.isArray(input)) return [];

  return input
    .filter((entry) => entry && typeof entry.day === "string")
    .map((entry) => ({
      day: entry.day,
      value: Array.isArray(entry.value)
        ? entry.value.map((point) => {
            if (!stripSnapshots || !isPlainObject(point)) return point;
            const { snapshot: _snapshot, ...compactPoint } = point;
            return compactPoint;
          })
        : [],
    }));
}

function collectNestedEntities(analyticData) {
  const entities = [];

  const walk = (node, path = []) => {
    if (!isPlainObject(node)) return;

    if (node.values_summary || node.alarms_by_tasa || node.tasa_summary) {
      entities.push({
        id: path.join(":") || node.name || `entity-${entities.length + 1}`,
        name: node.name || path[path.length - 1] || `entity-${entities.length + 1}`,
        valuesSummary: normalizeValuesSummary(node.values_summary),
        alarmBands: normalizeAlarmBands(node.alarms_by_tasa || node.tasa_summary),
        timeline: {
          graphActivity: pickTimelineSeries(node, "graph_activity"),
          graphFeeding: normalizeTimeSeries(node.graph_feeding, true),
          graphPerdidos: normalizeTimeSeries(node.graph_perdidos, true),
          tasaValues: normalizeTimeSeries(node.tasa_values, true),
          alarmsData: normalizeTimeSeries(node.alarms_data),
        },
        rawName: node.name || null,
      });
      return;
    }

    Object.entries(node).forEach(([key, inner]) => {
      if (!isPlainObject(inner)) return;
      walk(inner, [...path, key]);
    });
  };

  walk(analyticData);

  return entities;
}

function pickTimelineSeries(node, prefix) {
  return Object.entries(node)
    .filter(([key, value]) => key.startsWith(prefix) && isPlainObject(value))
    .map(([key, value]) => ({
      key,
      name: value.name || key,
      data: normalizeTimeSeries(value.data, true),
    }));
}

function aggregateValuesSummary(valuesSummaryList) {
  const perDay = new Map();

  valuesSummaryList.forEach((valuesSummary) => {
    normalizeValuesSummary(valuesSummary).forEach(({ day, value }) => {
      const current =
        perDay.get(day) || {
          sums: {},
          averages: {},
          counts: {},
        };

      METRIC_KEYS.forEach((metricKey) => {
        const metricValue = toNumber(value[metricKey]);
        if (SUM_METRICS.has(metricKey)) {
          current.sums[metricKey] = toNumber(current.sums[metricKey]) + metricValue;
        } else if (AVG_METRICS.has(metricKey)) {
          current.averages[metricKey] = toNumber(current.averages[metricKey]) + metricValue;
          current.counts[metricKey] = toNumber(current.counts[metricKey]) + 1;
        }
      });

      perDay.set(day, current);
    });
  });

  return Array.from(perDay.entries())
    .map(([day, bucket]) => {
      const value = {};
      METRIC_KEYS.forEach((metricKey) => {
        if (SUM_METRICS.has(metricKey)) {
          value[metricKey] = roundMetric(toNumber(bucket.sums[metricKey]));
        } else if (AVG_METRICS.has(metricKey)) {
          const count = toNumber(bucket.counts[metricKey]);
          value[metricKey] = roundMetric(
            count > 0 ? toNumber(bucket.averages[metricKey]) / count : 0
          );
        }
      });

      return { day, value };
    })
    .sort((left, right) => left.day.localeCompare(right.day));
}

function aggregateAlarmBands(alarmBandsList) {
  const result = new Map();

  alarmBandsList.forEach((alarmBands) => {
    normalizeAlarmBands(alarmBands).forEach(({ day, value }) => {
      const current = result.get(day) || { baja: 0, media: 0, alta: 0 };
      current.baja += toNumber(value.baja);
      current.media += toNumber(value.media);
      current.alta += toNumber(value.alta);
      result.set(day, current);
    });
  });

  return Array.from(result.entries()).map(([day, value]) => ({ day, value }));
}

function summarizeEntity(valuesSummary) {
  const summary = {
    dayCount: 0,
    metrics: {},
  };

  METRIC_KEYS.forEach((metricKey) => {
    summary.metrics[metricKey] = {
      value: 0,
      sampleCount: 0,
    };
  });

  normalizeValuesSummary(valuesSummary).forEach(({ value }) => {
    summary.dayCount += 1;
    METRIC_KEYS.forEach((metricKey) => {
      const metricValue = toNumber(value[metricKey]);
      if (SUM_METRICS.has(metricKey)) {
        summary.metrics[metricKey].value += metricValue;
      } else if (AVG_METRICS.has(metricKey)) {
        summary.metrics[metricKey].value += metricValue;
        summary.metrics[metricKey].sampleCount += 1;
      }
    });
  });

  const output = {};
  METRIC_KEYS.forEach((metricKey) => {
    const metricSummary = summary.metrics[metricKey];
    if (AVG_METRICS.has(metricKey)) {
      output[metricKey] = roundMetric(
        metricSummary.sampleCount > 0
          ? metricSummary.value / metricSummary.sampleCount
          : 0
      );
      return;
    }

    output[metricKey] = roundMetric(metricSummary.value);
  });

  return output;
}

function buildDashboardEntities({ data, layoutLevel }) {
  const analyticData = data?.analytic_data;
  if (!isPlainObject(analyticData)) return [];

  if (layoutLevel === "centro") {
    return Object.entries(analyticData)
      .map(([entityId, entity]) => ({
        id: entityId,
        name: entity?.name || entityId,
        valuesSummary: normalizeValuesSummary(entity?.values_summary),
        alarmBands: normalizeAlarmBands(entity?.alarms_by_tasa),
        timeline: {
          graphActivity: pickTimelineSeries(entity || {}, "graph_activity"),
          graphFeeding: normalizeTimeSeries(entity?.graph_feeding, true),
          graphPerdidos: normalizeTimeSeries(entity?.graph_perdidos, true),
          tasaValues: normalizeTimeSeries(entity?.tasa_values, true),
          alarmsData: normalizeTimeSeries(entity?.alarms_data),
        },
      }))
      .filter((entity) => entity.valuesSummary.length > 0 || entity.alarmBands.length > 0);
  }

  if (layoutLevel === "area" || layoutLevel === "region") {
    return collectNestedEntities(analyticData).map((entity) => ({
      ...entity,
      metrics: summarizeEntity(entity.valuesSummary),
    }));
  }

  return [];
}

function aggregateComparisonEntity(item, index) {
  if (item?.values_summary) {
    return {
      id: item.name || `comparison-${index + 1}`,
      name: item.name || `comparison-${index + 1}`,
      valuesSummary: normalizeValuesSummary(item.values_summary),
      alarmBands: normalizeAlarmBands(item.alarms_by_tasa || item.tasa_summary),
      timeline: {
        graphActivity: [],
        graphFeeding: [],
        graphPerdidos: [],
        tasaValues: [],
        alarmsData: [],
      },
    };
  }

  if (item?.analytic_data) {
    const nestedEntities = collectNestedEntities(item.analytic_data);
    return {
      id: item.name || `comparison-${index + 1}`,
      name: item.name || `comparison-${index + 1}`,
      valuesSummary: aggregateValuesSummary(
        nestedEntities.map((entity) => entity.valuesSummary)
      ),
      alarmBands: aggregateAlarmBands(nestedEntities.map((entity) => entity.alarmBands)),
      timeline: {
        graphActivity: [],
        graphFeeding: [],
        graphPerdidos: [],
        tasaValues: [],
        alarmsData: [],
      },
    };
  }

  return null;
}

function buildComparisonEntities({ data, versus, layoutLevel, selectedCenters }) {
  if (layoutLevel === "centro") {
    const dashboardEntities = buildDashboardEntities({ data, layoutLevel });
    const selected = Array.isArray(selectedCenters)
      ? new Set(selectedCenters.map(String))
      : new Set();

    return dashboardEntities.filter((entity) =>
      selected.size ? selected.has(String(entity.name)) || selected.has(String(entity.id)) : true
    );
  }

  if (!Array.isArray(versus)) return [];

  return versus
    .map((item, index) => aggregateComparisonEntity(item, index))
    .filter(Boolean);
}

function buildVisibleEntities(entities, selectedCenters) {
  const selected = Array.isArray(selectedCenters)
    ? new Set(selectedCenters.map(String))
    : new Set();

  if (!selected.size) {
    return entities.map((entity) => entity.name);
  }

  const filtered = entities
    .filter((entity) => selected.has(String(entity.id)) || selected.has(String(entity.name)))
    .map((entity) => entity.name);

  return filtered.length ? filtered : entities.map((entity) => entity.name);
}

function buildEntityTable(entities) {
  return entities.map((entity) => ({
    entityId: entity.id,
    name: entity.name,
    metrics: summarizeEntity(entity.valuesSummary),
  }));
}

function buildDailyRollup(entities) {
  return aggregateValuesSummary(entities.map((entity) => entity.valuesSummary));
}

function buildEntityDailySeries(entities, days) {
  return entities.map((entity) => {
    const byDay = new Map(
      normalizeValuesSummary(entity.valuesSummary).map(({ day, value }) => [day, value])
    );
    const values = {};
    METRIC_KEYS.forEach((m) => {
      values[m] = days.map((d) => roundMetric(toNumber(byDay.get(d)?.[m])));
    });
    return { entityId: entity.id, name: entity.name, values };
  });
}

function buildRankingSummary(entityTable) {
  const metricsToRank = [
    "alarms_count",
    "perdidos_count",
    "visitas_count",
    "sfr_value",
    "delivered_food",
  ];

  return metricsToRank.map((metricKey) => {
    const ordered = [...entityTable].sort(
      (left, right) =>
        toNumber(right.metrics?.[metricKey]) - toNumber(left.metrics?.[metricKey])
    );

    return {
      metric: metricKey,
      top: ordered.slice(0, 3).map((entry) => ({
        entityId: entry.entityId,
        name: entry.name,
        value: roundMetric(toNumber(entry.metrics?.[metricKey])),
      })),
      bottom: ordered
        .slice(-3)
        .reverse()
        .map((entry) => ({
          entityId: entry.entityId,
          name: entry.name,
          value: roundMetric(toNumber(entry.metrics?.[metricKey])),
        })),
    };
  });
}

function buildHighlights(entityTable, dailyRollup) {
  const highlights = [];

  const pushMaxHighlight = (metricKey, label) => {
    const winner = [...entityTable].sort(
      (left, right) =>
        toNumber(right.metrics?.[metricKey]) - toNumber(left.metrics?.[metricKey])
    )[0];
    if (!winner) return;

    highlights.push({
      type: "entity_metric_max",
      metric: metricKey,
      label,
      entityId: winner.entityId,
      entityName: winner.name,
      value: roundMetric(toNumber(winner.metrics?.[metricKey])),
    });
  };

  pushMaxHighlight("alarms_count", "Entidad con más alarmas");
  pushMaxHighlight("perdidos_count", "Entidad con mayor PPA");
  pushMaxHighlight("sfr_value", "Entidad con mayor SFR");

  const topAlarmDay = [...dailyRollup].sort(
    (left, right) =>
      toNumber(right.value?.alarms_count) - toNumber(left.value?.alarms_count)
  )[0];

  if (topAlarmDay) {
    highlights.push({
      type: "day_metric_max",
      metric: "alarms_count",
      label: "Día con más alarmas",
      day: topAlarmDay.day,
      value: roundMetric(toNumber(topAlarmDay.value?.alarms_count)),
    });
  }

  return highlights.slice(0, 6);
}

function buildViewNotes({ comparisonMode, simpleView, compact }) {
  const notes = [];

  if (comparisonMode === "versus") {
    notes.push("Vista de comparación activa.");
  }
  if (simpleView) {
    notes.push("Vista simple activa.");
  }
  if (compact) {
    notes.push("Modo compactado activo.");
  }
  notes.push("La fórmula exacta de Alarmas por visita no está confirmada.");

  return notes;
}

function buildTimelineWindows(entities, selectedEntityName, selectedDay) {
  const focusEntity =
    entities.find(
      (entity) => entity.name === selectedEntityName || entity.id === selectedEntityName
    ) || entities[0];

  if (!focusEntity) return [];

  const alarmSeries = focusEntity.timeline?.alarmsData || [];
  const feedingSeries = focusEntity.timeline?.graphFeeding || [];
  const lostSeries = focusEntity.timeline?.graphPerdidos || [];
  const tasaSeries = focusEntity.timeline?.tasaValues || [];

  const focusDay =
    selectedDay ||
    alarmSeries[alarmSeries.length - 1]?.day ||
    feedingSeries[feedingSeries.length - 1]?.day ||
    lostSeries[lostSeries.length - 1]?.day ||
    tasaSeries[tasaSeries.length - 1]?.day ||
    null;

  if (!focusDay) return [];

  const dayAlarms = alarmSeries.find((entry) => entry.day === focusDay)?.value || [];
  const dayLost = lostSeries.find((entry) => entry.day === focusDay)?.value || [];
  const dayTasa = tasaSeries.find((entry) => entry.day === focusDay)?.value || [];
  const dayFeeding = feedingSeries.find((entry) => entry.day === focusDay)?.value || [];

  const windows = [];

  if (dayAlarms.length) {
    dayAlarms.slice(0, 3).forEach((alarm, index) => {
      const start = alarm?.start || alarm?.time || null;
      windows.push({
        id: `alarm-${index + 1}`,
        reason: "alarms_cluster",
        start,
        end: alarm?.end || start,
        alarmsCount: 1,
        ppaDelta: 0,
        tasaAvg: 0,
        feedingPoints: 0,
      });
    });
  }

  if (!windows.length && dayLost.length) {
    const firstPoint = dayLost[0];
    const lastPoint = dayLost[dayLost.length - 1];
    windows.push({
      id: "lost-window",
      reason: "ppa_window",
      start: firstPoint?.time || null,
      end: lastPoint?.time || firstPoint?.time || null,
      alarmsCount: dayAlarms.length,
      ppaDelta:
        toNumber(lastPoint?.value) - toNumber(firstPoint?.value),
      tasaAvg:
        dayTasa.length > 0
          ? roundMetric(
              dayTasa.reduce((total, point) => total + toNumber(point?.value), 0) /
                dayTasa.length
            )
          : 0,
      feedingPoints: dayFeeding.length,
    });
  }

  return windows.slice(0, 3);
}

function createBlockRegistry() {
  const catalog = [];
  const blocks = {};

  const pushBlock = (block) => {
    if (!block || !block.id || !block.kind) return;
    if (catalog.length >= MAX_BLOCKS) return;
    if (blocks[block.id]) return;

    const payload = cloneDeepSafe(block.payload || {});
    const estimatedTokens = Math.max(60, Math.round(safeJsonByteLength(payload) / 4));

    catalog.push({
      id: block.id,
      kind: block.kind,
      view: block.view,
      entities: block.entities || [],
      metrics: block.metrics || [],
      days: block.days || [],
      priority: block.priority || 50,
      estimatedTokens,
    });

    blocks[block.id] = {
      kind: block.kind,
      payload,
    };
  };

  return { catalog, blocks, pushBlock };
}

function buildBlocks({
  view,
  comparisonMode,
  entities,
  entityTable,
  dailyRollup,
  rankingSummary,
  selectedEntityName,
  selectedDay,
}) {
  const registry = createBlockRegistry();

  registry.pushBlock({
    id: "entity-summary-main",
    kind: "entity_summary",
    view,
    entities: entityTable.slice(0, 8).map((entry) => entry.name),
    metrics: METRIC_KEYS,
    days: [],
    priority: 95,
    payload: {
      entityTable,
    },
  });

  ["alarms_count", "perdidos_count", "sfr_value"].forEach((metricKey, index) => {
    const ranking = rankingSummary.find((item) => item.metric === metricKey);
    if (!ranking) return;

    registry.pushBlock({
      id: `ranking-${metricKey}`,
      kind: "ranking_metric",
      view,
      entities: ranking.top.map((entry) => entry.name),
      metrics: [metricKey],
      days: [],
      priority: 90 - index,
      payload: ranking,
    });
  });

  ["alarms_count", "perdidos_count", "sfr_value"].forEach((metricKey, index) => {
    registry.pushBlock({
      id: `daily-${metricKey}-all`,
      kind: "daily_metric_series",
      view,
      entities: entities.slice(0, 6).map((entity) => entity.name),
      metrics: [metricKey],
      days: dailyRollup.map((entry) => entry.day),
      priority: 82 - index,
      payload: {
        metric: metricKey,
        series: dailyRollup.map((entry) => ({
          day: entry.day,
          value: roundMetric(toNumber(entry.value?.[metricKey])),
        })),
      },
    });
  });

  const entityDays = dailyRollup.map((entry) => entry.day);
  registry.pushBlock({
    id: "entity-daily-series-main",
    kind: "entity_daily_series",
    view,
    entities: entities.map((e) => e.name),
    metrics: METRIC_KEYS,
    days: entityDays,
    priority: 93,
    payload: {
      metrics: METRIC_KEYS,
      days: entityDays,
      series: buildEntityDailySeries(entities, entityDays),
    },
  });

  const alarmBandSeries = aggregateAlarmBands(entities.map((entity) => entity.alarmBands));
  if (alarmBandSeries.length) {
    registry.pushBlock({
      id: "alarm-band-distribution",
      kind: "alarm_band_distribution",
      view,
      entities: entities.slice(0, 6).map((entity) => entity.name),
      metrics: ["alarms_count"],
      days: alarmBandSeries.map((entry) => entry.day),
      priority: 84,
      payload: {
        days: alarmBandSeries,
      },
    });
  }

  if (comparisonMode === "versus") {
    registry.pushBlock({
      id: "comparison-matrix-main",
      kind: "comparison_matrix",
      view,
      entities: entityTable.map((entry) => entry.name),
      metrics: METRIC_KEYS,
      days: dailyRollup.map((entry) => entry.day),
      priority: 96,
      payload: {
        rows: entityTable,
      },
    });

    entityTable.slice(0, 4).forEach((entity, index) => {
      registry.pushBlock({
        id: `comparison-profile-${entity.entityId}`,
        kind: "comparison_entity_profile",
        view,
        entities: [entity.name],
        metrics: METRIC_KEYS,
        days: [],
        priority: 88 - index,
        payload: entity,
      });
    });

    registry.pushBlock({
      id: "comparison-radar-series",
      kind: "comparison_radar_series",
      view,
      entities: entityTable.map((entry) => entry.name),
      metrics: ["alarms_count", "perdidos_count", "sfr_value", "visitas_count"],
      days: dailyRollup.map((entry) => entry.day),
      priority: 74,
      payload: {
        metrics: ["alarms_count", "perdidos_count", "sfr_value", "visitas_count"],
        rows: entityTable.map((entry) => ({
          entityId: entry.entityId,
          name: entry.name,
          values: entry.metrics,
        })),
      },
    });

    registry.pushBlock({
      id: "comparison-advanced-points",
      kind: "comparison_advanced_points",
      view,
      entities: entityTable.map((entry) => entry.name),
      metrics: ["alarms_count", "perdidos_count", "sfr_value"],
      days: dailyRollup.map((entry) => entry.day),
      priority: 68,
      payload: {
        points: dailyRollup.slice(0, 12),
      },
    });
  } else {
    const windows = buildTimelineWindows(entities, selectedEntityName, selectedDay);
    if (windows.length) {
      registry.pushBlock({
        id: "timeline-windows-main",
        kind: "timeline_windows",
        view,
        entities: [selectedEntityName || entities[0]?.name].filter(Boolean),
        metrics: ["alarms_count", "perdidos_count", "sfr_value"],
        days: selectedDay ? [selectedDay] : [],
        priority: 86,
        payload: {
          entityName: selectedEntityName || entities[0]?.name || null,
          day: selectedDay || null,
          windows,
        },
      });
    }

    const focusEntity =
      entities.find(
        (entity) => entity.name === selectedEntityName || entity.id === selectedEntityName
      ) || entities[0];

    if (focusEntity) {
      registry.pushBlock({
        id: `entity-timeline-${focusEntity.id}`,
        kind: "entity_timeline_focus",
        view,
        entities: [focusEntity.name],
        metrics: ["alarms_count", "perdidos_count", "sfr_value"],
        days: focusEntity.valuesSummary.map((entry) => entry.day),
        priority: 78,
        payload: {
          entityId: focusEntity.id,
          entityName: focusEntity.name,
          valuesSummary: focusEntity.valuesSummary.slice(-10),
          alarmBands: focusEntity.alarmBands.slice(-10),
        },
      });
    }
  }

  if (view === "simple") {
    ["alarms_count", "alarms_by_ton", "alarms_by_visits", "perdidos_count", "sfr_value"].forEach(
      (metricKey, index) => {
        registry.pushBlock({
          id: `simple-card-${metricKey}`,
          kind: index === 0 ? "simple_card_summary" : "simple_card_sparkline",
          view,
          entities: entityTable.slice(0, 6).map((entry) => entry.name),
          metrics: [metricKey],
          days: dailyRollup.map((entry) => entry.day),
          priority: 80 - index,
          payload: {
            metric: metricKey,
            value: roundMetric(
              entityTable.reduce(
                (total, entry) => total + toNumber(entry.metrics?.[metricKey]),
                0
              )
            ),
            sparkline: dailyRollup.map((entry) => ({
              day: entry.day,
              value: roundMetric(toNumber(entry.value?.[metricKey])),
            })),
            ranking:
              rankingSummary.find((item) => item.metric === metricKey)?.top || [],
          },
        });
      }
    );

    registry.pushBlock({
      id: "simple-card-ranking-main",
      kind: "simple_card_ranking",
      view,
      entities: entityTable.map((entry) => entry.name),
      metrics: ["alarms_count", "perdidos_count", "sfr_value"],
      days: [],
      priority: 72,
      payload: {
        rankingSummary,
      },
    });
  }

  registry.catalog.sort((left, right) => right.priority - left.priority);

  if (registry.catalog.length > MAX_INITIAL_BLOCK_CANDIDATES) {
    const allowedIds = new Set(
      registry.catalog.slice(0, MAX_INITIAL_BLOCK_CANDIDATES).map((entry) => entry.id)
    );
    registry.catalog.splice(
      MAX_INITIAL_BLOCK_CANDIDATES,
      registry.catalog.length - MAX_INITIAL_BLOCK_CANDIDATES
    );
    Object.keys(registry.blocks).forEach((blockId) => {
      if (!allowedIds.has(blockId)) {
        delete registry.blocks[blockId];
      }
    });
  }

  return registry;
}

function applyTransportBudget(context) {
  const next = cloneDeepSafe(context);
  next.coverage.transportBytes = safeJsonByteLength(next);

  if (next.coverage.transportBytes <= MAX_CONTEXT_BYTES) {
    return next;
  }

  const orderedCatalog = [...next.catalog].sort(
    (left, right) => left.priority - right.priority
  );

  while (orderedCatalog.length && safeJsonByteLength(next) > MAX_CONTEXT_BYTES) {
    const lowest = orderedCatalog.shift();
    next.catalog = next.catalog.filter((entry) => entry.id !== lowest.id);
    delete next.blocks[lowest.id];
  }

  next.coverage.truncated = true;
  next.coverage.warnings = [
    ...(Array.isArray(next.coverage.warnings) ? next.coverage.warnings : []),
    "low_priority_blocks_omitted",
  ];
  next.coverage.transportBytes = safeJsonByteLength(next);

  return next;
}

export function buildAssistantScappContext({
  viewContext,
  data,
  versus,
  selectedCenters,
  selectedDay,
  jaulaSeleccionada,
  loading,
}) {
  if (loading) {
    return {
      schemaVersion: SCAPP_CONTEXT_SCHEMA_VERSION,
      state: "loading",
      source: "visible-ui",
    };
  }

  try {
    const comparisonMode = viewContext?.uiState?.comparisonMode || "dashboard";
    const layoutLevel = viewContext?.uiState?.layoutLevel || "centro";
    const simpleView = Boolean(viewContext?.uiState?.simpleView);
    const compact = Boolean(viewContext?.uiState?.compact);
    const view = viewContext?.view || "general";
    const currentData =
      comparisonMode === "versus"
        ? buildComparisonEntities({
            data,
            versus,
            layoutLevel,
            selectedCenters,
          })
        : buildDashboardEntities({
            data,
            layoutLevel,
          });

    if (!currentData.length) {
      return {
        schemaVersion: SCAPP_CONTEXT_SCHEMA_VERSION,
        state: "empty",
        source: "visible-ui",
      };
    }

    const entityTable = buildEntityTable(currentData);
    const dailyRollup = buildDailyRollup(currentData);
    const rankingSummary = buildRankingSummary(entityTable);
    const highlights = buildHighlights(entityTable, dailyRollup);
    const viewNotes = buildViewNotes({
      comparisonMode,
      simpleView,
      compact,
    });
    const blockRegistry = buildBlocks({
      view,
      comparisonMode,
      entities: currentData,
      entityTable,
      dailyRollup,
      rankingSummary,
      selectedEntityName: jaulaSeleccionada,
      selectedDay,
    });

    const context = {
      schemaVersion: SCAPP_CONTEXT_SCHEMA_VERSION,
      state: "ready",
      source: "visible-ui",
      viewSignature: {
        service: viewContext?.service || "scapp",
        module: viewContext?.module || "analytics",
        view,
        layoutLevel,
        comparisonMode,
        compact,
        simpleView,
        selectedEntityName: jaulaSeleccionada || null,
        selectedDay: selectedDay || null,
        dateRange: {
          startDate: viewContext?.filters?.dias?.startDate || null,
          endDate: viewContext?.filters?.dias?.endDate || null,
          dayRange: toNumber(viewContext?.filters?.dias?.dayRange),
        },
        entities: {
          region: viewContext?.entities?.region || "",
          area: viewContext?.entities?.area || "",
          centro: viewContext?.entities?.centro || "",
          selected: Array.isArray(selectedCenters)
            ? selectedCenters.slice(0, 8)
            : [],
          visible: buildVisibleEntities(currentData, selectedCenters),
        },
      },
      coverage: {
        visibleEntityCount: currentData.length,
        loadedDayCount: dailyRollup.length,
        missingDays: [],
        warnings: [],
        truncated: false,
        transportBytes: 0,
      },
      digest: {
        metricDefinitions: METRIC_DEFINITIONS,
        entityTable,
        dailyRollup,
        rankingSummary,
        highlights,
        viewNotes,
      },
      catalog: blockRegistry.catalog,
      blocks: blockRegistry.blocks,
    };

    const budgeted = applyTransportBudget(context);
    budgeted.rawGraphSeries = currentData.map((entity) => ({
      entityId: entity.id,
      name: entity.name,
      graphFeeding: entity.timeline?.graphFeeding || [],
      graphPerdidos: entity.timeline?.graphPerdidos || [],
      tasaValues: entity.timeline?.tasaValues || [],
      alarmsData: entity.timeline?.alarmsData || [],
    }));
    return budgeted;
  } catch (error) {
    return {
      schemaVersion: SCAPP_CONTEXT_SCHEMA_VERSION,
      state: "error",
      source: "visible-ui",
      errorCode: "build_failed",
      errorMessage: error instanceof Error ? error.message : "unknown_error",
    };
  }
}
